/**
 * TeamEditorModal.js — full in-app team editing (replaces the /teams EJS page).
 *
 * Part of the "Express becomes a pure API server" plan: every action here goes
 * through the JSON API (teamsAPI / accountsAPI / uploadsAPI). This is an EDIT
 * surface — it never touches playback or the timeline; the pitch simply
 * re-renders when the teams state refreshes.
 *
 * The 3D character preview reuses characterEditorPreview (same asset cache as
 * the pitch), so what you edit is exactly what renders on the pitch.
 *
 * Props:
 * - teams:        raw DB teams (from /auth/session) — _id, name, colors, playerInfo…
 * - account:      the signed-in account ({ _id }) — needed to create teams.
 * - homeTeamId, awayTeamId: current assignments (highlight + quick apply).
 * - onClose:            () => void
 * - onTeamsChanged:     () => void — refetch the session (updates the pitch too).
 * - onApply:            (teamId, 'home' | 'away') => void
 */

import { useEffect, useRef, useState } from 'react';
import { teamsAPI, accountsAPI, uploadsAPI, getTeamLogoUrl, getMediaUrl } from './api';
import { createPreview } from './characterEditorPreview';
import { useI18n } from './i18n';
import ConfirmModal from './ConfirmModal';
import LookEditorModal from './LookEditorModal';

// A real squad: 11 starters + substitutes + the spare keepers. The backend has
// no cap of its own, so the editor sets the limit and says so out loud.
export const MAX_SQUAD = 26;

// The five downloadable print layouts (kit templates) the website offers.
// [template file, schema field for the upload, label key]
const KIT_TEMPLATES = [
  ['template_shirt_front.png', 'shirtFront', 'teams.shirt'],
  ['template_shirt_back.png', 'shirtBack', 'teams.shirt'],
  ['template_shorts_front.png', 'shortsFront', 'teams.shorts'],
  ['template_shorts_back.png', 'shortsBack', 'teams.shorts'],
  ['template_socks.png', 'socksFront', 'teams.socks'],
];

const input = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px',
  background: '#0f172a', border: '1px solid #475569', borderRadius: 6,
  color: '#e2e8f0', fontSize: 12.5, outline: 'none',
};
const label = { fontSize: 10.5, color: '#94a3b8', margin: '8px 0 3px', display: 'block' };
const btn = (bg, fg = '#fff') => ({
  padding: '8px 13px', border: 'none', borderRadius: 6, cursor: 'pointer',
  background: bg, color: fg, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
});

export default function TeamEditorModal({
  teams, account, homeTeamId, awayTeamId, onClose, onTeamsChanged, onApply,
}) {
  const { t } = useI18n();
  const [selectedId, setSelectedId] = useState(
    () => homeTeamId || awayTeamId || teams[0]?._id || null,
  );
  const [draft, setDraft] = useState(null);
  const [playerIdx, setPlayerIdx] = useState(0);
  const [styles, setStyles] = useState(null); // { skinStyles, hairStyles }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [lookOpen, setLookOpen] = useState(false);

  const previewRef = useRef(null);
  const containerRef = useRef(null);

  // ── Style catalog (skin/hair ids + labels) — fetched once per open ──
  useEffect(() => {
    let live = true;
    uploadsAPI.styles().then(s => { if (live) setStyles(s); }).catch(() => {});
    return () => { live = false; };
  }, []);

  // ── 3D preview lifecycle (same renderer the pitch uses) ──
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return undefined;
    let pv = null;
    try { pv = createPreview(c); } catch (e) { console.error('Character preview failed:', e); }
    previewRef.current = pv;
    return () => { pv?.destroy(); previewRef.current = null; };
  }, [!!draft]);

  // Draft follows the selected team (fresh from the API so uploads show up).
  const reloadDraft = async (id) => {
    try {
      const team = await teamsAPI.get(id);
      // NEVER eat typing: if the user started editing this team while the
      // fetch was in flight, keep their draft (and its snapshot) as-is.
      const cur = draftRef.current;
      if (cur && cur._id === id && snapOf(cur) !== baselineRef.current) return;
      setDraft(team);
      applyBaseline(snapOf(team));
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    }
  };
  useEffect(() => {
    if (!selectedId) { setDraft(null); return; }
    const local = teams.find(x => x._id === selectedId);
    if (local) { setDraft(local); applyBaseline(snapOf(local)); }
    reloadDraft(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Preview follows the selected player + kit.
  useEffect(() => {
    const pv = previewRef.current;
    if (!pv || !draft) return;
    const p = (draft.playerInfo || [])[playerIdx] || {};
    pv.update({
      name: p.name || '', number: p.number || String(playerIdx + 1), position: p.position || '',
      skinStyle: p.skinStyle || 'style_000', hairStyle: p.hairStyle || 'style_000',
      numberColor: draft.numberColor || '#ffffff',
      teamTextures: {
        shirtFront: draft.shirtFront || '', shirtBack: draft.shirtBack || '',
        shortsFront: draft.shortsFront || '', shortsBack: draft.shortsBack || '',
        socksFront: draft.socksFront || '',
        gkShirtFront: draft.gkShirtFront || '', gkShirtBack: draft.gkShirtBack || '',
        gkShortsFront: draft.gkShortsFront || '', gkShortsBack: draft.gkShortsBack || '',
        gkSocksFront: draft.gkSocksFront || '',
      },
    }).catch(() => {});
  }, [draft, playerIdx]);

  // ── UNSAVED-WORK GUARD ────────────────────────────────────────────────────
  // The cheapest gestures in this modal (click the backdrop, press Esc, click
  // another team chip) used to DISCARD a roster you had just typed — silently.
  // One snapshot of the loaded team decides: if the draft differs, the exit
  // asks first. Save refreshes the snapshot, so a saved team closes clean.
  const snapOf = (d) => (d ? JSON.stringify({
    name: d.name || '', badge: d.badge || '', numberColor: d.numberColor || '',
    playerInfo: d.playerInfo || [],
  }) : '');
  const [baseline, setBaseline] = useState('');
  const dirty = !!draft && snapOf(draft) !== baseline;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  // Refs mirror the live draft/snapshot so async work can respect typing that
  // started while a fetch was in flight (see reloadDraft).
  const draftRef = useRef(null);
  draftRef.current = draft;
  const baselineRef = useRef('');
  const applyBaseline = (v) => { baselineRef.current = v; setBaseline(v); };

  // One in-app yes/no for the two moments that can lose work: leaving with
  // unsaved edits, and deleting a team. window.confirm() put a system strip on
  // top of the editor — unstyled, tiny on a phone, and silent about what is
  // actually lost.
  const [confirmState, setConfirmState] = useState(null);
  const confirmOpenRef = useRef(false);
  confirmOpenRef.current = !!confirmState;
  const askDiscard = (then) => {
    if (!dirtyRef.current) { then(); return; }
    setConfirmState({
      title: t('confirm.discardTitle'),
      message: t('confirm.discardBody'),
      confirmLabel: t('confirm.discard'),
      danger: true,
      onConfirm: () => { setConfirmState(null); then(); },
      onCancel: () => setConfirmState(null),
    });
  };
  const requestClose = () => askDiscard(onClose);
  const switchTeam = (id) => {
    if (id === selectedId) return;
    askDiscard(() => setSelectedId(id));
  };

  // Esc belongs to THIS modal while it is open: handled in the capture phase
  // so App's global Esc cascade (which closes modals without asking) never
  // sees the key. While a confirm is open the confirm owns Esc instead —
  // otherwise the key would replace one question with another.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmOpenRef.current) return;
      e.stopPropagation();
      requestClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (field, value) => setDraft(d => (d ? { ...d, [field]: value } : d));
  const setPlayerField = (field, value) => setDraft(d => {
    if (!d) return d;
    const playerInfo = [...(d.playerInfo || [])];
    if (!playerInfo[playerIdx]) return d;
    playerInfo[playerIdx] = { ...playerInfo[playerIdx], [field]: value };
    return { ...d, playerInfo };
  });

  const save = async () => {
    if (!draft) return;
    setBusy(true); setMsg(null);
    try {
      await teamsAPI.update(draft._id, {
        name: draft.name, badge: draft.badge,
        // Kit colours are decided by the textures now — color1–4 are legacy and
        // are left exactly as they are in the database (the API skips undefined).
        numberColor: draft.numberColor, playerInfo: draft.playerInfo,
      });
      // The draft is now the truth — closing after a save must not ask.
      applyBaseline(snapOf(draft));
      onTeamsChanged();
      setMsg({ kind: 'ok', text: t('teams.saved') });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally { setBusy(false); }
  };

  const createTeam = () => {
    if (!account?._id) return;
    // Creating switches the selection — the same discard risk as a team-chip click.
    askDiscard(doCreateTeam);
  };
  const doCreateTeam = async () => {
    setBusy(true); setMsg(null);
    try {
      const team = await accountsAPI.createTeam(account._id, { name: `Team ${teams.length + 1}` });
      onTeamsChanged();
      setSelectedId(team._id);
      setDraft(team);
      applyBaseline(snapOf(team));
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally { setBusy(false); }
  };

  const deleteTeam = () => {
    if (!draft) return;
    setConfirmState({
      title: t('confirm.deleteTeam'),
      message: t('confirm.deleteTeamBody', { name: draft.name }),
      confirmLabel: t('confirm.deleteTeam'),
      danger: true,
      onConfirm: () => { setConfirmState(null); doDeleteTeam(); },
      onCancel: () => setConfirmState(null),
    });
  };
  const doDeleteTeam = async () => {
    setBusy(true); setMsg(null);
    try {
      await teamsAPI.delete(draft._id);
      onTeamsChanged();
      const rest = teams.filter(x => x._id !== draft._id);
      setSelectedId(rest[0]?._id || null);
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally { setBusy(false); }
  };

  const uploadBadge = async (file) => {
    if (!file || !draft) return;
    setBusy(true);
    try { await uploadsAPI.uploadTeamLogo(draft._id, file); await reloadDraft(draft._id); onTeamsChanged(); }
    catch (e) { setMsg({ kind: 'err', text: e.message }); }
    finally { setBusy(false); }
  };
  // Player photos are no longer an editing feature — the 3D figure IS the
  // player now. The `photo` field stays in the schema and getPlayerPhotoUrl
  // still resolves it, so teams saved with a photo keep loading it.
  // (Both verbs read `draft` directly: calling setPlayerIdx/setMsg from inside a
  // setDraft updater would run them twice under StrictMode — and add two
  // players on one click.)
  const addPlayer = () => {
    if (!draft) return;
    const playerInfo = [...(draft.playerInfo || [])];
    if (playerInfo.length >= MAX_SQUAD) {
      setMsg({ kind: 'err', text: t('teams.squadFull', { max: MAX_SQUAD }) });
      return;
    }
    // Next free number (numbers must be unique in the team, so skip taken ones).
    const used = new Set(playerInfo.map(p => String(p.number || '').trim()));
    let n = 1; while (used.has(String(n)) && n < 99) n++;
    playerInfo.push({ name: '', number: String(n), position: '', photo: '', skinStyle: 'style_000', hairStyle: 'style_000' });
    setDraft({ ...draft, playerInfo });
    setPlayerIdx(playerInfo.length - 1);   // land on the new player — type the name next
    setMsg({ kind: 'ok', text: t('teams.playerAdded', { name: `#${n}`, n: playerInfo.length }) });
  };
  const removePlayer = () => {
    if (!draft) return;
    const playerInfo = [...(draft.playerInfo || [])];
    if (playerInfo.length <= 1) { setMsg({ kind: 'err', text: t('teams.squadMin') }); return; }
    const [gone] = playerInfo.splice(playerIdx, 1);
    setDraft({ ...draft, playerInfo });
    setPlayerIdx(i => Math.max(0, Math.min(i, playerInfo.length - 1)));
    setMsg({ kind: 'ok', text: t('teams.playerRemoved', { name: gone?.name || `#${gone?.number || ''}`, n: playerInfo.length }) });
  };

  // ── Kit textures (multipart POST /uploads/team/:id/textures) ──
  // [schema field, multipart field, chip label] — ✓ = custom texture uploaded.
  const TEXTURE_FIELDS = [
    ['shirtFront', 'shirt_front', '👕 F'],
    ['shirtBack', 'shirt_back', '👕 B'],
    ['shortsFront', 'shorts_front', '🩳 F'],
    ['shortsBack', 'shorts_back', '🩳 B'],
    ['socksFront', 'socks_front', '🧦 F'],
    ['gkShirtFront', 'gk_shirt_front', 'GK👕 F'],
    ['gkShirtBack', 'gk_shirt_back', 'GK👕 B'],
    ['gkShortsFront', 'gk_shorts_front', 'GK🩳 F'],
    ['gkShortsBack', 'gk_shorts_back', 'GK🩳 B'],
    ['gkSocksFront', 'gk_socks_front', 'GK🧦 F'],
  ];
  const uploadTexture = async (schemaField, file) => {
    if (!file || !draft) return;
    setBusy(true);
    try { await uploadsAPI.uploadTeamTextures(draft._id, { [schemaField]: file }); await reloadDraft(draft._id); onTeamsChanged(); }
    catch (e) { setMsg({ kind: 'err', text: e.message }); }
    finally { setBusy(false); }
  };
  const clearTexture = async (multipartField) => {
    if (!draft) return;
    setBusy(true);
    try { await uploadsAPI.clearTeamTexture(draft._id, multipartField); await reloadDraft(draft._id); onTeamsChanged(); }
    catch (e) { setMsg({ kind: 'err', text: e.message }); }
    finally { setBusy(false); }
  };

  const player = draft?.playerInfo?.[playerIdx];
  const skins = styles ? [...styles.skinStyles].sort((a, b) => (a.toneOrder ?? 99) - (b.toneOrder ?? 99)) : [];
  const hairs = styles ? styles.hairStyles : [];
  const isOwner = draft ? draft.isOwner !== false : false;

  // Roster data entry: Enter in any player field advances to the NEXT player
  // (focus stays in the same field), so naming an XI is type · Enter · type ·
  // Enter — no trip back to the number chips. No wrap: the last player stays.
  const rosterSize = (draft?.playerInfo || []).length;
  const nextPlayerKey = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    setPlayerIdx(i => Math.min(i + 1, rosterSize - 1));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.7)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }} onClick={requestClose}>
      <div className="kv-team-editor-card" onClick={e => e.stopPropagation()} onKeyDown={(e) => {
        // Ctrl+S / Cmd+S saves from anywhere in the modal — the instinctive
        // chord, and it stops the browser's own Save page.
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          if (draft && !busy) save();
        }
      }} style={{
        background: '#1e293b', borderRadius: 12, padding: '1.1rem 1.3rem',
        width: '100%', maxWidth: 860, color: '#e2e8f0',
        maxHeight: 'calc(var(--kv-viewport-h) * 0.9)', display: 'flex', flexDirection: 'column',
      }}>
        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{t('teams.title')}</h2>
          <button onClick={requestClose} title="Esc" style={{
            background: '#475569', border: 'none', borderRadius: 6, color: '#fff',
            width: 30, height: 30, cursor: 'pointer',
          }}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{t('teams.hint')}</div>

        {/* ── Team strip ── */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
          {teams.map(team => (
            <button key={team._id} type="button" onClick={() => switchTeam(team._id)} style={{
              ...btn(team._id === selectedId ? '#2563eb' : '#334155'),
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            }}>
              {/* The badge, not four legacy colour dots — kit colours come from
                  the textures now, so the dots showed values nobody can set. */}
              <img src={getTeamLogoUrl(team)} alt="" style={{
                width: 18, height: 18, borderRadius: 4, objectFit: 'cover',
                background: '#0f172a', border: '1px solid #475569',
              }} />
              {team.name}
              {(team._id === homeTeamId || team._id === awayTeamId) && <span style={{ color: '#7dd3fc' }}>·</span>}
            </button>
          ))}
          <button type="button" onClick={createTeam} disabled={busy} style={{ ...btn('#16a34a'), flexShrink: 0 }}>{t('teams.new')}</button>
        </div>

        {msg && (
          <div style={{
            marginTop: 8, padding: '6px 10px', borderRadius: 6, fontSize: 12,
            background: msg.kind === 'ok' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: msg.kind === 'ok' ? '#4ade80' : '#f87171',
          }}>{msg.text}</div>
        )}

        {draft && (
          <div className="kv-team-editor-body" style={{ display: 'flex', gap: 16, marginTop: 10, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {/* ── Left: identity + kit ── */}
            <div className="kv-team-editor-identity" style={{ width: 250, flexShrink: 0 }}>
              <label style={label}>{t('teams.name')}</label>
              <input style={input} value={draft.name || ''} onChange={e => setField('name', e.target.value)} />

              <label style={label}>{t('teams.numberColor')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="color" value={draft.numberColor || '#ffffff'}
                  onChange={e => setField('numberColor', e.target.value)}
                  title={t('teams.numberColorHint')}
                  aria-label={t('teams.numberColor')}
                  style={{ width: 40, height: 28, padding: 0, border: '1px solid #475569', borderRadius: 4, background: '#0f172a', cursor: 'pointer' }} />
                <span style={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.35 }}>{t('teams.numberColorHint')}</span>
              </div>

              <label style={label}>{t('teams.badge')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={getTeamLogoUrl(draft)} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', background: '#0f172a', border: '1px solid #334155' }} />
                <label style={{ ...btn('#334155'), display: 'inline-block' }}>
                  {t('teams.logo')}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => uploadBadge(e.target.files?.[0])} />
                </label>
              </div>

              <label style={label}>{t('teams.textures')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {TEXTURE_FIELDS.map(([schemaField, multipartField, chip]) => (
                  <span key={schemaField} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 2,
                    background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '2px 3px',
                  }}>
                    <label style={{
                      ...btn(draft[schemaField] ? '#14532d' : '#334155'),
                      padding: '4px 7px', fontSize: 11, display: 'inline-block', cursor: 'pointer',
                    }}>
                      {chip}{draft[schemaField] ? ' ✓' : ''}
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => uploadTexture(schemaField, e.target.files?.[0])} />
                    </label>
                    {draft[schemaField] && (
                      <button type="button" title="✕" onClick={() => clearTexture(multipartField)} disabled={busy}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 11, padding: 2 }}>✕</button>
                    )}
                  </span>
                ))}
              </div>

              {/* The print layouts the textures are designed on — the same five
                  files the website offers, so a design made anywhere fits. */}
              <label style={label}>{t('teams.templates')}</label>
              <div style={{ fontSize: 10.5, color: '#64748b', marginBottom: 4, lineHeight: 1.35 }}>
                {t('teams.templatesHint')}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {KIT_TEMPLATES.map(([file, schemaField, labelKey]) => (
                  <a key={file} href={getMediaUrl(`/uploads/viz-assets/templates/${file}`)}
                    download={file} title={draft[schemaField] ? t('teams.customTex') : t('teams.defaultTex')}
                    style={{ ...btn('#334155'), padding: '5px 9px', fontSize: 11, textDecoration: 'none', display: 'inline-block' }}>
                    {t('teams.download', { name: t(labelKey) })}
                  </a>
                ))}
              </div>

              {isOwner && (
                <button type="button" onClick={deleteTeam} disabled={busy} style={{ ...btn('#7f1d1d', '#fecaca'), marginTop: 14 }}>{t('teams.delete')}</button>
              )}
            </div>

            {/* ── Right: player + character preview ── */}
            <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Player chips + squad size. The chips are the roster: tap one to
                  edit that player, + to reach the bench (up to MAX_SQUAD). */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                {(draft.playerInfo || []).map((p, i) => (
                  <button key={i} type="button" onClick={() => setPlayerIdx(i)} style={{
                    width: 34, height: 30, borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 800,
                    border: i === playerIdx ? '2px solid #38bdf8' : '1px solid #475569',
                    background: i === playerIdx ? '#0c4a6e' : '#0f172a', color: '#e2e8f0',
                  }}>{p.number || i + 1}</button>
                ))}
                <button type="button" onClick={addPlayer} disabled={busy || (draft.playerInfo || []).length >= MAX_SQUAD}
                  title={(draft.playerInfo || []).length >= MAX_SQUAD ? t('teams.squadFull', { max: MAX_SQUAD }) : ''}
                  style={{ ...btn('#16a34a'), padding: '5px 9px', fontSize: 11,
                    opacity: (draft.playerInfo || []).length >= MAX_SQUAD ? 0.4 : 1 }}>
                  {t('teams.addPlayer')}
                </button>
                <span style={{ fontSize: 10.5, color: '#64748b' }}>
                  {t('teams.squadCount', { n: (draft.playerInfo || []).length, max: MAX_SQUAD })}
                </span>
              </div>

              {player && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={label}>{t('teams.number')}</label>
                    <input style={input} value={player.number || ''} title={t('teams.enterNext')}
                      onChange={e => setPlayerField('number', e.target.value)} onKeyDown={nextPlayerKey} />
                  </div>
                  <div>
                    <label style={label}>{t('teams.position')}</label>
                    <input style={input} value={player.position || ''} title={t('teams.enterNext')}
                      onChange={e => setPlayerField('position', e.target.value)} onKeyDown={nextPlayerKey} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={label}>{t('teams.player')}</label>
                    <input style={input} value={player.name || ''} title={t('teams.enterNext')}
                      onChange={e => setPlayerField('name', e.target.value)} onKeyDown={nextPlayerKey} />
                  </div>
                  {/* The look editor replaces two 200-option dropdowns: one
                      button, then a preview of the figure in this team's kit. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, gridColumn: '1 / -1' }}>
                    <button type="button" onClick={() => setLookOpen(true)}
                      aria-label={t('look.editAria', { name: player.name || `#${player.number || playerIdx + 1}` })}
                      style={btn('#1d4ed8')}>
                      {t('look.edit')}
                    </button>
                    <span style={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.35 }}>
                      {t('look.current', {
                        skin: skins.find(s => s.id === (player.skinStyle || 'style_000'))?.label || player.skinStyle || 'style_000',
                        hair: hairs.find(h => h.id === (player.hairStyle || 'style_000'))?.label || player.hairStyle || 'style_000',
                      })}
                    </span>
                    <button type="button" onClick={removePlayer} disabled={busy || (draft.playerInfo || []).length <= 1}
                      title={t('teams.removePlayer')} aria-label={t('teams.removePlayer')}
                      style={{ ...btn('#7f1d1d', '#fecaca'), padding: '6px 10px', fontSize: 11, marginLeft: 'auto',
                        opacity: (draft.playerInfo || []).length <= 1 ? 0.4 : 1 }}>
                      🗑
                    </button>
                  </div>
                </div>
              )}

              {/* Live 3D preview of the selected player + kit */}
              <div ref={containerRef} style={{
                flex: '1 1 auto', minHeight: 180, borderRadius: 8, overflow: 'hidden',
                border: '1px solid #334155',
              }} />
            </div>
          </div>
        )}

        {/* ── Footer: save + quick apply ── */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" disabled={!draft || busy} onClick={() => onApply(draft._id, 'home')}
            style={{ ...btn('#1d4ed8'), opacity: draft ? 1 : 0.4 }}>{t('teams.applyHome')}</button>
          <button type="button" disabled={!draft || busy} onClick={() => onApply(draft._id, 'away')}
            style={{ ...btn('#b91c1c'), opacity: draft ? 1 : 0.4 }}>{t('teams.applyAway')}</button>
          <button type="button" disabled={!draft || busy} onClick={save}
            title={dirty ? `Ctrl+S — ${t('teams.unsaved')}` : 'Ctrl+S'}
            style={{ ...btn('#16a34a'), opacity: draft ? 1 : 0.4 }}>{t('teams.save')}{dirty ? ' •' : ''}</button>
        </div>
      </div>
      {lookOpen && player && (
        <LookEditorModal
          player={player}
          team={draft}
          styles={styles}
          onClose={() => setLookOpen(false)}
          onApply={(look) => {
            setDraft(d => {
              if (!d) return d;
              const playerInfo = [...(d.playerInfo || [])];
              if (!playerInfo[playerIdx]) return d;
              playerInfo[playerIdx] = { ...playerInfo[playerIdx], skinStyle: look.skinStyle, hairStyle: look.hairStyle };
              return { ...d, playerInfo };
            });
            setLookOpen(false);
          }}
        />
      )}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          onConfirm={confirmState.onConfirm}
          onCancel={confirmState.onCancel}
        />
      )}
    </div>
  );
}
